/**
 * Durable per-page "last-chosen view" store for the Documents and Work Items
 * list pages.
 *
 * Both pages hold their active view (the `?tab=` toggle) in the URL, which makes
 * it shareable but never remembered: a fresh load — and in the Penrose IDE the
 * iframe is reloaded as a full document on navigation — drops the user back to
 * the hard-coded default. This store records the user's chosen view locally so
 * the next visit (without an explicit `?tab=` in the URL) restores it.
 *
 * Precedence at the call sites: an explicit `?tab=` in the URL wins (share /
 * deep links keep working), then this stored preference, then the page's
 * hard-coded fallback.
 *
 * Scope is global per browser / IDE instance (NOT per-org), matching the
 * existing column-visibility prefs (`penrose:documents:cols:*`,
 * `penrose:work-items:cols`). A future Supabase `user_view_settings` table would
 * add cross-device sync; this delivers the local half.
 *
 * Mirrors `lastUsedAgentConfig.ts` / `theme.ts`: all access is guarded so a
 * denied / throwing / absent localStorage (SSR, private mode, a security
 * policy, quota) degrades to a silent no-op (writes) / `null` (reads). Never
 * throws.
 */

import type { DocumentsTab } from './models/documents';
import type { ProjectsTab } from './models/projects';
import type { WorkItemsTab } from '../components/work-items/types';

/** localStorage keys, under the public `penrose:` namespace. */
export const DOCUMENTS_VIEW_KEY = 'penrose:documents:view';
export const WORK_ITEMS_VIEW_KEY = 'penrose:work-items:view';
export const PROJECTS_VIEW_KEY = 'penrose:projects:view';

/**
 * The Work Items views that are worth persisting. `'timeline'` is deliberately
 * excluded — it is a "Coming soon" placeholder, so restoring a returning user
 * onto it would be a dead-end.
 */
const PERSISTABLE_WORK_ITEMS_VIEWS: ReadonlyArray<WorkItemsTab> = ['board', 'table'];
const DOCUMENTS_VIEWS: ReadonlyArray<DocumentsTab> = ['table', 'gallery'];
const PROJECTS_VIEWS: ReadonlyArray<ProjectsTab> = ['gallery', 'table'];

/**
 * Resolve the visible column / chip keys for a list view, defending against
 * *foreign* keys that don't belong to this surface.
 *
 * The list VMs round-trip view state (incl. `?cols=`) through the shared
 * `window.location.search`, which under the app's `HashRouter` persists across
 * route changes. That let one list page's column keys (e.g. the Documents
 * table's `title,type,lifecycle,…`) leak into another's state (e.g. `/projects`)
 * — where every key matched nothing, so the gallery rendered chip-less cards and
 * the table rendered with no columns at all.
 *
 * Precedence mirrors the call sites: validated `stateCols` → validated parsed
 * `storedCols` → per-tab `defaults`. A source that yields no *valid* keys is
 * skipped (not trusted blindly), so contamination can never strand a view.
 */
export function resolveVisibleProperties(
  stateCols: ReadonlyArray<string>,
  storedCols: string | null,
  validKeys: ReadonlyArray<string>,
  defaults: ReadonlyArray<string>
): ReadonlyArray<string> {
  const valid = new Set(validKeys);

  const fromState = stateCols.filter((k) => valid.has(k));
  if (fromState.length > 0) return fromState;

  if (storedCols) {
    const fromStore = storedCols
      .split(',')
      .map((s) => s.trim())
      .filter((k) => valid.has(k));
    if (fromStore.length > 0) return fromStore;
  }

  return [...defaults];
}

/**
 * Best-effort access to `window.localStorage`. Returns `null` when storage is
 * unavailable (SSR, private mode, a security policy that throws on access), in
 * which case every operation degrades to a no-op / `null`.
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read the stored Documents view, or `null` when none is stored / the stored
 * value is not a valid view / storage is unavailable. Never throws.
 */
export function getDocumentsView(): DocumentsTab | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(DOCUMENTS_VIEW_KEY);
    return DOCUMENTS_VIEWS.includes(raw as DocumentsTab)
      ? (raw as DocumentsTab)
      : null;
  } catch {
    return null;
  }
}

/**
 * Persist the chosen Documents view. Best-effort: a quota / private-mode /
 * denied write is a silent no-op. Never throws.
 */
export function setDocumentsView(view: DocumentsTab): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(DOCUMENTS_VIEW_KEY, view);
  } catch {
    // Non-fatal — losing the write only means the next visit falls back to the
    // page default.
  }
}

/**
 * Read the stored Work Items view, or `null` when none is stored / the stored
 * value is not a persistable view (including a stale `'timeline'`) / storage is
 * unavailable. Never throws.
 */
export function getWorkItemsView(): WorkItemsTab | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(WORK_ITEMS_VIEW_KEY);
    return PERSISTABLE_WORK_ITEMS_VIEWS.includes(raw as WorkItemsTab)
      ? (raw as WorkItemsTab)
      : null;
  } catch {
    return null;
  }
}

/**
 * Persist the chosen Work Items view. `'timeline'` is ignored (not persistable;
 * see above). Best-effort: a quota / private-mode / denied write is a silent
 * no-op. Never throws.
 */
export function setWorkItemsView(view: WorkItemsTab): void {
  if (!PERSISTABLE_WORK_ITEMS_VIEWS.includes(view)) return;
  const store = storage();
  if (!store) return;
  try {
    store.setItem(WORK_ITEMS_VIEW_KEY, view);
  } catch {
    // Non-fatal — see setDocumentsView.
  }
}

/**
 * Read the stored Projects view, or `null` when none is stored / the stored
 * value is not a valid view / storage is unavailable. Never throws.
 */
export function getProjectsView(): ProjectsTab | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(PROJECTS_VIEW_KEY);
    return PROJECTS_VIEWS.includes(raw as ProjectsTab)
      ? (raw as ProjectsTab)
      : null;
  } catch {
    return null;
  }
}

/**
 * Persist the chosen Projects view. Best-effort: a quota / private-mode /
 * denied write is a silent no-op. Never throws.
 */
export function setProjectsView(view: ProjectsTab): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PROJECTS_VIEW_KEY, view);
  } catch {
    // Non-fatal — see setDocumentsView.
  }
}
